import { Table } from "@tanstack/react-table"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DataPesertaKepemimpinanSmbjm, PaginationData } from "@/types"
import { useEffect } from "react"
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from "react-icons/hi2"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  paginationData: PaginationData<DataPesertaKepemimpinanSmbjm[]>
}

export function Pagination<TData>({
  table,
  paginationData,
}: DataTablePaginationProps<TData>) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const rowsPerPageParams = searchParams.get("rowsPerPage")
  // const searchQueryParams = searchParams.get("search");
  // const idsRefJalurJenisParams = searchParams.get("ids_ref_jalur_jenis");
  // const idsUkParams = searchParams.get("ids_uk");
  // const statusValidasiParams = searchParams.get("status_validasi");
  // const statusKelulusanParams = searchParams.get("status_kelulusan");
  // const statusDaftarkembaliParams = searchParams.get("status_daftarkembali");

  const {
    current_page = 1,
    last_page = 1,
    per_page = 10,
    total = 0,
  } = paginationData || {}

  useEffect(() => {
    if (!rowsPerPageParams) return

    table.setPageSize(Number(rowsPerPageParams))
    searchParams.set("page", "1")
    searchParams.set("rowsPerPage", rowsPerPageParams)

    navigate(`${pathname}?${searchParams.toString()}`)
  }, [pathname, rowsPerPageParams, table, navigate, searchParams])

  return (
    <div className="flex flex-wrap items-center justify-between px-2">
      <div className="flex-1 flex gap-3 text-sm text-muted-foreground">
        <span>
          {table.getFilteredSelectedRowModel().rows.length} of {per_page} row(s)
          selected.
        </span>
        <span>{total} total record.</span>
      </div>
      <div className="flex flex-wrap items-center space-x-6 md:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              const newPageSize = Number(value)

              searchParams.set("rowsPerPage", newPageSize.toString())
              searchParams.set("page", "1")

              navigate(`${pathname}?${searchParams.toString()}`)
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {current_page} of {paginationData?.last_page}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 md:flex"
            onClick={() => {
              searchParams.set("page", "1")
              navigate(`${pathname}?${searchParams.toString()}`)
            }}
            disabled={current_page === 1}
          >
            <span className="sr-only">Go to first page</span>
            <HiChevronDoubleLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              if (current_page > 1) {
                searchParams.set("page", String(Number(current_page - 1)))
                navigate(`${pathname}?${searchParams.toString()}`)
              }
            }}
            disabled={current_page === 1}
          >
            <span className="sr-only">Go to previous page</span>
            <HiOutlineChevronLeft />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              if (current_page < last_page) {
                searchParams.set("page", String(Number(current_page + 1)))
                navigate(`${pathname}?${searchParams.toString()}`)
              }
            }}
            disabled={current_page === last_page}
          >
            <span className="sr-only">Go to next page</span>
            <HiOutlineChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 md:flex"
            onClick={() => {
              if (current_page < last_page) {
                searchParams.set("page", String(last_page))
                navigate(`${pathname}?${searchParams.toString()}`)
              }
            }}
            disabled={current_page === last_page}
          >
            <span className="sr-only">Go to last page</span>
            <HiChevronDoubleRight />
          </Button>
        </div>
      </div>
    </div>
  )
}
